import {useMessage} from '@coolcolduk/ui-mui';
import {ContentCopy} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {useCallback, useState} from 'react';
import {ExamModule} from '@cp/model';
import DropdownDifficulty from '../../atoms/dropdownDifficulty/DropdownDifficulty';
import BondModuleTopicDropdown from '../../bonds/bondModuleTopicDropdown/BondModuleTopicDropdown';
import {promptsGenerateQuestion} from '../../api/callable/prompts';
import {parseAiGeneratedQuestion} from '../../pages/admin/questionNew/util/parseAiGeneratedQuestion';

export interface GeneratePromptDialogProps {
  open: boolean;
  examModules: ExamModule[];
  modulesLoading: boolean;
  onClose: () => void;
  onApply: (
    parsedQuestion: {
      questionText: string;
      hint?: string;
      options: string[];
      answer: string;
      explanation: string;
    },
    moduleId: string,
    topic: string,
    difficulty: number,
  ) => void;
}

export default function GeneratePromptDialog({
  open,
  examModules,
  modulesLoading,
  onClose,
  onApply,
}: GeneratePromptDialogProps) {
  const {showMessage} = useMessage();
  const [moduleId, setModuleId] = useState<string | undefined>(undefined);
  const [topic, setTopic] = useState<string | undefined>(undefined);
  const [difficulty, setDifficulty] = useState<number | undefined>(1);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [aiOutput, setAiOutput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleModuleChange = useCallback(
    (newModuleId?: string) => {
      setModuleId(newModuleId);
      setTopic(undefined); // Reset topic when module changes
    },
    [setModuleId, setTopic],
  );

  const handleCreatePrompt = useCallback(async () => {
    if (!moduleId || !topic || !difficulty) {
      showMessage('Please select a module and topic', 6000, 'error');
      return;
    }

    setLoading(true);
    try {
      const prompts = await promptsGenerateQuestion({moduleId, topic, difficulty});
      setSystemPrompt(prompts.system);
      setUserPrompt(prompts.user);
    } catch (error) {
      console.error('Error generating prompts:', error);
      showMessage('Failed to generate prompts. Please try again.', 6000, 'error');
    } finally {
      setLoading(false);
    }
  }, [moduleId, topic, difficulty, showMessage]);

  const handleCopySystemPrompt = useCallback(() => {
    navigator.clipboard.writeText(systemPrompt);
    showMessage('System prompt copied to clipboard', 3000, 'success');
  }, [systemPrompt, showMessage]);

  const handleCopyUserPrompt = useCallback(() => {
    navigator.clipboard.writeText(userPrompt);
    showMessage('User prompt copied to clipboard', 3000, 'success');
  }, [userPrompt, showMessage]);

  const handleAiOutputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAiOutput(e.target.value);
  }, []);

  const handleApply = useCallback(() => {
    if (!difficulty) {
      showMessage('Please select a difficulty', 6000, 'error');
      return;
    }

    if (!aiOutput.trim()) {
      showMessage('Please paste the AI-generated output', 6000, 'error');
      return;
    }

    if (!moduleId || !topic) {
      showMessage('Please ensure module and topic are selected', 6000, 'error');
      return;
    }

    try {
      const parsed = parseAiGeneratedQuestion(aiOutput);
      onApply(parsed, moduleId, topic, difficulty);
      showMessage('Question applied successfully!', 6000, 'success');
      onClose();
    } catch (error) {
      console.error('Error parsing AI output:', error);
      showMessage(
        `Failed to parse AI output: ${error instanceof Error ? error.message : 'Unknown error'}`,
        6000,
        'error',
      );
    }
  }, [aiOutput, moduleId, topic, difficulty, onApply, onClose, showMessage]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{fontWeight: 600, fontSize: '1.25rem'}}>Generate Question Prompts</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{pt: 2}}>
          <BondModuleTopicDropdown
            moduleId={moduleId}
            topic={topic}
            examModules={examModules}
            modulesLoading={modulesLoading}
            hideTopicWhenNoModule
            onModuleChange={handleModuleChange}
            onTopicChange={setTopic}
          />

          <DropdownDifficulty difficulty={difficulty} onChange={setDifficulty} disableAnyDifficulty />

          <Button
            variant="contained"
            onClick={handleCreatePrompt}
            disabled={!moduleId || !topic || loading}
            sx={{
              bgcolor: '#FF6F59',
              '&:hover': {bgcolor: '#FF6F59', opacity: 0.8},
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Create Prompt'}
          </Button>

          {systemPrompt && (
            <Box>
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                  System Prompt
                </Typography>
                <Tooltip title="Copy system prompt">
                  <IconButton onClick={handleCopySystemPrompt} size="small">
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={systemPrompt}
                InputProps={{readOnly: true}}
                sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
              />
            </Box>
          )}

          {userPrompt && (
            <Box>
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                  User Prompt
                </Typography>
                <Tooltip title="Copy user prompt">
                  <IconButton onClick={handleCopyUserPrompt} size="small">
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={userPrompt}
                InputProps={{readOnly: true}}
                sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
              />
            </Box>
          )}

          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <Typography variant="h6">Paste AI-Generated Output</Typography>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={aiOutput}
              onChange={handleAiOutputChange}
              placeholder="Paste the AI-generated question output here (JSON or JSONL format)..."
              sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
            />
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!aiOutput.trim()}
              sx={{
                bgcolor: '#FF6F59',
                '&:hover': {bgcolor: '#FF6F59', opacity: 0.8},
                alignSelf: 'flex-start',
              }}
            >
              Apply
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
